import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import useModal from '~/hooks/useModal';
import {
  selectCurrentOrganizationId,
  selectSubscription,
  selectSubscriptionSchedule,
} from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';
import analytics, { TRACK_EVENT_ARG_TYPE } from '~/services/analytics';

import { CancelDowngradeModal } from '../CancelDowngradeModal/CancelDowngradeModal';
import { getSubscriptionPlanLabel, isLowerPlan } from '../Subscription/Subscription.helpers';

import * as S from './CancelDowngradeCard.styled';

import CryingFaceLogo from '~/images/crying-face.svg?react';

export const CancelDowngradeCard = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'downgradeCard' });
  const activeSubscription = useAppSelector(selectSubscription);
  const subscriptionSchedule = useAppSelector(selectSubscriptionSchedule);
  const organizationId = useAppSelector(selectCurrentOrganizationId);
  const modal = useModal();

  if (!activeSubscription || !subscriptionSchedule) return null;

  const activeParallelAddon = activeSubscription.addons.find(
    (addon) => addon.type === 'extra_parallel_runs_limit',
  );
  const scheduleParallelAddon = subscriptionSchedule.addons.find(
    (addon) => addon.type === 'extra_parallel_runs_limit',
  );

  const includeBillingCycle = subscriptionSchedule.billingCycle !== activeSubscription.billingCycle;
  const isPlanDowngrade = isLowerPlan(subscriptionSchedule, activeSubscription);

  const cancelDowngrade = () => {
    analytics.trackEvent('cancel_downgrade_button_clicked', {
      [TRACK_EVENT_ARG_TYPE.BUTTON_TYPE]: 'cancel_downgrade',
      [TRACK_EVENT_ARG_TYPE.ORGANIZATION_ID]: organizationId,
      [TRACK_EVENT_ARG_TYPE.ACCOUNT_PLAN]: activeSubscription.type,
      [TRACK_EVENT_ARG_TYPE.PLAN_DOWNGRADED_TO]: subscriptionSchedule.type,
      [TRACK_EVENT_ARG_TYPE.DOWNGRADE_TYPE]: isPlanDowngrade
        ? 'plan_downgrade'
        : 'parallel_runs_downgrade',
    });
    modal.showCustom(CancelDowngradeModal);
  };

  return (
    <S.Container>
      <CryingFaceLogo width={32} height={32} />
      <div>
        <S.Title>{t('title', "You've downgraded your plan")}</S.Title>
        <S.Description>
          {t(
            'plansInfo',
            'Your plan will be downgraded from {{currentPlan}} to {{incomingPlan}} on {{date}}.',
            {
              currentPlan: getSubscriptionPlanLabel({
                plan: activeSubscription,
                parallels: activeParallelAddon?.value ?? 0,
                includeBillingCycle,
              }),
              incomingPlan: getSubscriptionPlanLabel({
                plan: subscriptionSchedule,
                parallels: scheduleParallelAddon?.value ?? 0,
                includeBillingCycle,
              }),
              date: dayjs(activeSubscription.end).format('DD MMM YYYY'),
            },
          )}
        </S.Description>
        <S.Description>
          {t('cancelInfo', 'You can still change your mind and keep your previous subscription.')}
        </S.Description>
      </div>
      <S.CancelDowngradeButton variant="primary" onClick={cancelDowngrade}>
        {t('cancelDowngrade', 'Cancel the downgrade')}
      </S.CancelDowngradeButton>
    </S.Container>
  );
};
